import {
    BrandElementsFontsClass,
    IBrandElementFont,
    IDesignElements,
} from '../interfaces/brand-magic.interface';
import { ITemplate } from '../interfaces/campaign.interface';
import { TemplateChildFields } from '../interfaces/template.interface';
import { normalizeColor } from './color.utils';

export const addCorrectFontsInTemplate = (json: any, brandElementData: IDesignElements | null) => {
    if (!brandElementData) {
        return json;
    }
    const brandDataFonts = brandElementData && brandElementData.fonts ? brandElementData.fonts : {};
    const fontsMap: { [key: string]: string } = {};
    for (const key in brandDataFonts) {
        if (brandDataFonts.hasOwnProperty(key)) {
            const values = brandDataFonts[key as BrandElementsFontsClass];
            if (values) {
                values.forEach((font) => {
                    if (font.url) {
                        fontsMap[font.fontFamily] = font.url;
                    }
                });
            }
        }
    }

    let newFonts: IBrandElementFont[] = [];
    if ('fonts' in json) {
        newFonts = [...json.fonts];
    }

    json.pages.forEach((page: any) => {
        page.children.forEach((child: any) => {
            if (child.type.toLowerCase() === 'text' && 'fontFamily' in child) {
                if (
                    fontsMap[child.fontFamily] &&
                    !newFonts.find((f) => f.fontFamily === child.fontFamily)
                ) {
                    newFonts.push({
                        fontFamily: child.fontFamily,
                        url: fontsMap[child.fontFamily],
                    });
                }
            }
        });
    });

    json.fonts = Array.from(new Map(newFonts.map((font) => [font.fontFamily, font])).values());
    return json;
};

export const getTemplateWithShuffledColor = (
    template: any,
    colorMapping: Record<string, string>,
) => {
    const pages = template.pages;

    const updatedPages = pages.map((oldPage: any) => {
        const page = { ...oldPage };
        const children = page.children;

        const updatedChildren = children.map((oldChild: any) => {
            const child = { ...oldChild };
            if (TemplateChildFields.STROKE in child) {
                child[TemplateChildFields.STROKE] = getNewColor(
                    child[TemplateChildFields.STROKE],
                    colorMapping,
                );
            }

            if (TemplateChildFields.BORDER_COLOR in child) {
                child[TemplateChildFields.BORDER_COLOR] = getNewColor(
                    child[TemplateChildFields.BORDER_COLOR],
                    colorMapping,
                );
            }

            if (TemplateChildFields.COLOR in child) {
                child[TemplateChildFields.COLOR] = getNewColor(
                    child[TemplateChildFields.COLOR],
                    colorMapping,
                );
            }

            if (TemplateChildFields.FILL in child) {
                child[TemplateChildFields.FILL] = getNewColor(
                    child[TemplateChildFields.FILL],
                    colorMapping,
                );
            }

            if (TemplateChildFields.COLORS_REPLACE in child) {
                const colorsReplace = child[TemplateChildFields.COLORS_REPLACE];
                child[TemplateChildFields.COLORS_REPLACE] = Object.fromEntries(
                    Object.entries(colorsReplace).map(([key, color]) => [
                        key,
                        getNewColor(color as string, colorMapping),
                    ]),
                );
            }

            return child;
        });
        page.children = updatedChildren;
        return page;
    });

    template.pages = updatedPages;

    return template;
};

const getNewColor = (oldColor: string, colorMapping: Record<string, string>) => {
    const isLinearGradient = oldColor.indexOf('linear-gradient') !== -1;

    if (isLinearGradient) {
        const regex = /rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+(?:\s*,\s*[\d.]+\s*)?\)/gi;
        return oldColor.replace(regex, (match) => {
            const normalizedMatch = normalizeColor(match);
            return colorMapping[normalizedMatch] || match;
        });
    } else {
        const normalizedOldColor = normalizeColor(oldColor);
        if (colorMapping[normalizedOldColor]) return colorMapping[normalizedOldColor];
        return oldColor;
    }
    return oldColor;
};

export const getTemplateFromId = (
    templateId: string | undefined,
    templates: ITemplate[] | undefined,
): ITemplate | null => {
    if (!templates || templates.length === 0 || !templateId || templateId === '') {
        return null;
    }
    const searchedTemplate = templates.find((template) => {
        return template.id === templateId;
    });
    return searchedTemplate ? searchedTemplate : null;
};
